import React from 'react';
import {
  PieChart, Pie, Cell, ResponsiveContainer
} from 'recharts';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme
} from '@mui/material';

const UserCurrencies = ({ funds, value }) => {
  const theme = useTheme();
  
  // Subtle color palette (monochromatic with different tones)
  const colors = [
    theme.palette.primary.light,
    theme.palette.primary.main,
    theme.palette.secondary.light,
    theme.palette.secondary.main,
    theme.palette.success.light,
    theme.palette.success.main,
    theme.palette.info.light,
    theme.palette.info.main,
    theme.palette.warning.light,
    theme.palette.warning.main,
  ];

  const calculatePortfolioWorth = () => {
    let totalBtcValue = 0;
    let totalUsdValue = 0;
    const portfolioRows = [];
    const pieChartData = [];
    let otherUsdValue = 0;

    const btcUsdtPrice = value.find(item => item.Symbol.class_name === "BTCUSDT")?.symbol_price || 0;

    funds.forEach(item => {
      let btcValue = 0;
      const assetAmount = parseFloat(item.free) + parseFloat(item.locked);
      
      if (item.asset === "BTC") {
        btcValue = assetAmount;
      } else {
        const btcPair = value.find(item_value => item_value.Symbol.class_name === `${item.asset}BTC`);
        const inversePair = value.find(item_value => item_value.Symbol.class_name === `BTC${item.asset}`);
        
        if (btcPair) {
          btcValue = assetAmount * parseFloat(btcPair.symbol_price);
        } else if (inversePair) {
          btcValue = assetAmount / parseFloat(inversePair.symbol_price);
        }
      }

      const usdValue = btcValue * parseFloat(btcUsdtPrice);
      totalBtcValue += btcValue;
      totalUsdValue += usdValue;

      portfolioRows.push({
        asset: item.asset,
        quantity: assetAmount,
        btcWorth: btcValue,
        usdWorth: usdValue
      });

      if (usdValue > 10.0) {
        pieChartData.push({ 
          name: item.asset, 
          value: usdValue 
        });
      } else {
        otherUsdValue += usdValue;
      }
    });

    if (otherUsdValue > 0) {
      pieChartData.push({ name: 'Other', value: otherUsdValue });
    }

    return {
      portfolioRows,
      totalBtcValue,
      totalUsdValue,
      pieChartData
    };
  };

  const portfolioData = calculatePortfolioWorth();

  const renderCustomizedLabel = ({
    cx, cy, midAngle, innerRadius, outerRadius, percent, index
  }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 1.2;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill={theme.palette.text.secondary}
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={12}
        fontWeight={500}
      >
        {`${portfolioData.pieChartData[index].name} (${(percent * 100).toFixed(1)}%)`}
      </text>
    );
  };

  return (
    <Box sx={{ 
      width: '100%', 
      mb: 4,
      backgroundColor: theme.palette.background.default,
      p: 3,
      borderRadius: 2,
      boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.05)'
    }}>
      <Typography 
        variant="h6" 
        gutterBottom
        sx={{
          color: theme.palette.text.primary,
          fontWeight: 600,
          mb: 3
        }}
      >
        Portfolio Overview
      </Typography>
      
      {/* Summary Cards */}
      <Box sx={{ 
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2,
        mb: 4
      }}>
        <Paper sx={{ 
          flex: 1, 
          p: 2,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 1
        }}>
          <Typography variant="body2" color="text.secondary">
            Account
          </Typography>
          <Typography variant="body1" fontWeight={500}>
            {localStorage.getItem('email')}
          </Typography>
        </Paper>
        
        <Paper sx={{ 
          flex: 1, 
          p: 2,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 1
        }}>
          <Typography variant="body2" color="text.secondary">
            Total Value (USD)
          </Typography>
          <Typography variant="body1" fontWeight={500}>
            ${portfolioData.totalUsdValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        </Paper>
        
        <Paper sx={{ 
          flex: 1, 
          p: 2,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 1
        }}>
          <Typography variant="body2" color="text.secondary">
            Total Value (BTC)
          </Typography>
          <Typography variant="body1" fontWeight={500}>
            {portfolioData.totalBtcValue.toFixed(8)}
          </Typography>
        </Paper>
      </Box>

      {/* Content Area */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', lg: 'row' },
        gap: 4
      }}>
        {/* Portfolio Table */}
        <Box sx={{ 
          flex: 1,
          minWidth: 0 // Prevent overflow
        }}>
          <TableContainer 
            component={Paper}
            sx={{
              boxShadow: 'none',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.background.paper }}>
                  <TableCell sx={{ fontWeight: 600 }}>Asset</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Quantity</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>BTC Value</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>USD Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {portfolioData.portfolioRows.map((row, index) => (
                  <TableRow 
                    key={index}
                    sx={{ 
                      '&:nth-of-type(even)': {
                        backgroundColor: theme.palette.action.hover
                      }
                    }}
                  >
                    <TableCell>{row.asset}</TableCell>
                    <TableCell align="right">{row.quantity.toFixed(8)}</TableCell>
                    <TableCell align="right">{row.btcWorth.toFixed(6)}</TableCell>
                    <TableCell align="right">
                      ${row.usdWorth.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Pie Chart */}
        <Box sx={{ 
          flex: 1,
          height: 400,
          minWidth: 300,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              mb: 2,
              color: theme.palette.text.secondary,
              alignSelf: 'flex-start'
            }}
          >
            Asset Allocation
          </Typography>
          <Box sx={{ 
            width: '100%', 
            height: '100%',
            backgroundColor: theme.palette.background.paper,
            borderRadius: 1,
            p: 2,
            border: `1px solid ${theme.palette.divider}`
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={portfolioData.pieChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={120}
                  innerRadius={70}
                  paddingAngle={1}
                  dataKey="value"
                >
                  {portfolioData.pieChartData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={colors[index % colors.length]} 
                      stroke={theme.palette.background.paper}
                      strokeWidth={2}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default UserCurrencies;